import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { EditorJsField } from '@/components/editor/EditorJsField'
import { ImageUploadField } from '@/components/common/ImageUploadField'
import { newsApi, type News, type NewsInput, type NewsTag } from '@/api/news'
import { getErrorMessage } from '@/utils/error'
import type { EditorJsData } from '@/types'

const slugRegex = /^[a-z0-9-]*$/

const createNewsSchema = (t: (key: string) => string) =>
  z.object({
    title: z
      .string()
      .min(1, t('admin.news.validation.titleRequired'))
      .max(300, t('admin.news.validation.titleMaxLength')),
    slug: z
      .string()
      .max(200, t('admin.news.validation.slugMaxLength'))
      .refine((val) => slugRegex.test(val), t('admin.news.validation.slugFormat'))
      .optional()
      .or(z.literal('')),
    preview: z.string().max(1000, t('admin.news.validation.previewMaxLength')).optional(),
    is_pinned: z.boolean(),
    meta_title: z.string().max(200, t('admin.news.validation.metaTitleMaxLength')).optional(),
    meta_description: z
      .string()
      .max(500, t('admin.news.validation.metaDescriptionMaxLength'))
      .optional(),
  })

type NewsFormData = z.infer<ReturnType<typeof createNewsSchema>>

const emptyBody: EditorJsData = { blocks: [] }

interface NewsEditorDialogProps {
  open: boolean
  /** null — создание новой новости */
  newsId: number | null
  tags: NewsTag[]
  onClose: () => void
  /** Вызывается после успешного сохранения (для перезагрузки списка) */
  onSaved: (news: News) => void
}

/**
 * Модалка создания/редактирования новости: блочный редактор (Editor.js),
 * обложка, теги, SEO-поля в свёрнутой секции.
 */
export function NewsEditorDialog({ open, newsId, tags, onClose, onSaved }: NewsEditorDialogProps) {
  const { t } = useTranslation()
  const isEdit = newsId !== null

  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [seoOpen, setSeoOpen] = useState(false)

  const [body, setBody] = useState<EditorJsData>(emptyBody)
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverRemoved, setCoverRemoved] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsFormData>({
    resolver: zodResolver(createNewsSchema(t)),
    defaultValues: {
      title: '',
      slug: '',
      preview: '',
      is_pinned: false,
      meta_title: '',
      meta_description: '',
    },
  })

  useEffect(() => {
    if (!open) return

    // Сброс к дефолтам при каждом открытии
    setSaveError(null)
    setLoadError(null)
    setSeoOpen(false)
    setCoverFile(null)
    setCoverRemoved(false)

    if (newsId === null) {
      reset({ title: '', slug: '', preview: '', is_pinned: false, meta_title: '', meta_description: '' })
      setBody(emptyBody)
      setSelectedTagIds([])
      setCoverUrl(null)
      return
    }

    // Асинхронный префетч: модалка уже открыта, внутри Skeleton
    setLoading(true)
    newsApi
      .getOne(newsId)
      .then((news) => {
        reset({
          title: news.title,
          slug: news.slug,
          preview: news.preview ?? '',
          is_pinned: news.is_pinned,
          meta_title: news.meta_title ?? '',
          meta_description: news.meta_description ?? '',
        })
        setBody(news.body_json ?? emptyBody)
        setSelectedTagIds((news.tags ?? []).map((tag) => tag.id))
        setCoverUrl(news.cover_url)
      })
      .catch((error) => setLoadError(getErrorMessage(error)))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, newsId])

  const handleCoverChange = (file: File | null) => {
    setCoverFile(file)
    setCoverRemoved(file === null)
  }

  const submit = async (data: NewsFormData) => {
    if (!body.blocks || body.blocks.length === 0 || body.blocks.every((b) => !b.data || Object.keys(b.data).length === 0)) {
      setSaveError(t('admin.news.validation.bodyRequired'))
      return
    }

    setSaving(true)
    setSaveError(null)

    const payload: NewsInput = {
      title: data.title,
      slug: data.slug && data.slug.trim() !== '' ? data.slug : null,
      preview: data.preview && data.preview.trim() !== '' ? data.preview : null,
      body_json: body,
      is_pinned: data.is_pinned,
      meta_title: data.meta_title && data.meta_title.trim() !== '' ? data.meta_title : null,
      meta_description:
        data.meta_description && data.meta_description.trim() !== '' ? data.meta_description : null,
      tag_ids: selectedTagIds,
    }

    if (coverRemoved && !coverFile) {
      payload.cover_path = null
    }

    let saved: News
    try {
      saved = isEdit ? await newsApi.update(newsId, payload) : await newsApi.create(payload)
    } catch (error) {
      setSaveError(getErrorMessage(error))
      setSaving(false)
      return
    }

    if (coverFile) {
      try {
        saved = await newsApi.uploadCover(saved.id, coverFile)
      } catch (error) {
        // Новость уже сохранена — не теряем её, но честно говорим про обложку
        setSaving(false)
        setSaveError(t('admin.news.coverUploadFailed', { error: getErrorMessage(error) }))
        onSaved(saved)
        return
      }
    }

    setSaving(false)
    onSaved(saved)
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit ? t('admin.news.editTitle') : t('admin.news.createTitle')}
      </DialogTitle>
      <DialogContent>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={200} />
            <Skeleton variant="rounded" height={40} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('admin.news.fields.title')}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  fullWidth
                  required
                  size="small"
                />
              )}
            />

            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('admin.news.fields.slug')}
                  placeholder={t('admin.news.fields.slugPlaceholder')}
                  error={!!errors.slug}
                  helperText={errors.slug?.message ?? t('admin.news.fields.slugHelp')}
                  fullWidth
                  size="small"
                />
              )}
            />

            <Controller
              name="preview"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('admin.news.fields.preview')}
                  helperText={errors.preview?.message ?? t('admin.news.fields.previewHelp')}
                  error={!!errors.preview}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                />
              )}
            />

            <EditorJsField
              label={t('admin.news.fields.body')}
              value={body}
              onChange={setBody}
              rows={8}
              initDelayMs={80}
              imageUploader={(file) => newsApi.uploadImage(file)}
            />

            <ImageUploadField
              value={coverUrl}
              onChange={handleCoverChange}
              label={t('admin.news.fields.cover')}
              disabled={saving}
            />

            <Autocomplete
              multiple
              size="small"
              options={tags}
              value={tags.filter((tag) => selectedTagIds.includes(tag.id))}
              onChange={(_, value) => setSelectedTagIds(value.map((tag) => tag.id))}
              getOptionLabel={(tag) => tag.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((tag, index) => (
                  <Chip
                    label={tag.name}
                    size="small"
                    sx={{ bgcolor: tag.color, color: '#fff' }}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label={t('admin.news.fields.tags')} size="small" />
              )}
            />

            <Controller
              name="is_pinned"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={t('admin.news.fields.isPinned')}
                />
              )}
            />

            <Box>
              <Button
                size="small"
                onClick={() => setSeoOpen((prev) => !prev)}
                endIcon={seoOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none' }}
              >
                {t('admin.news.fields.seoSection')}
              </Button>
              <Collapse in={seoOpen}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.news.fields.seoHelp')}
                  </Typography>
                  <Controller
                    name="meta_title"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('admin.news.fields.metaTitle')}
                        error={!!errors.meta_title}
                        helperText={errors.meta_title?.message}
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="meta_description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('admin.news.fields.metaDescription')}
                        error={!!errors.meta_description}
                        helperText={errors.meta_description?.message}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                      />
                    )}
                  />
                </Box>
              </Collapse>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(submit)}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
